import { useStore } from "@nanostores/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { type ApiToken, getAllBridgedTokens, getAllTokens, getTokenByAddress } from "@turnstile-portal/turnstile.js";
import type { Address } from "viem";
import { getL1Portal } from "../lib/singletons";
import { isAztecConnected } from "../state/aztecWallet";
import { getL1ClientAsync } from "../wagmi";
import { useAccount } from "wagmi";

export enum TokenProposalStatus {
  UNKNOWN = 0,
  PROPOSED = 1,
  ACCEPTED = 2,
  REJECTED = 3,
}

export type TokenDetails = {
  tokenAddress: Address; // L1 address
  l2Address?: Address; // L2 address (only for bridged tokens)
  tokenName: string;
  tokenSymbol: string;
  tokenDecimals: number;
  l1ProposalStatus: TokenProposalStatus;
  l1RegistrationStatus: boolean;
  l2RegistrationStatus: boolean;
};

// Map API token status to our TokenProposalStatus enum
function mapApiStatusToProposalStatus(status: string | null | undefined): TokenProposalStatus {
  switch (status) {
    case "PROPOSED":
      return TokenProposalStatus.PROPOSED;
    case "ACCEPTED":
      return TokenProposalStatus.ACCEPTED;
    case "REJECTED":
      return TokenProposalStatus.REJECTED;
    default:
      return TokenProposalStatus.UNKNOWN;
  }
}

// Convert API Token to TokenDetails
function mapApiTokenToTokenDetails(token: ApiToken): TokenDetails {
  return {
    tokenAddress: token.l1_address as Address,
    l2Address: token.l2_address as Address | undefined, // Include L2 address from API
    tokenName: token.name || "",
    tokenSymbol: token.symbol || "",
    tokenDecimals: token.decimals || 18,
    l1ProposalStatus: mapApiStatusToProposalStatus(token.l1_allow_list_status),
    l1RegistrationStatus: token.l1_registration_block !== null && token.l1_registration_block !== undefined,
    l2RegistrationStatus: token.l2_registration_block !== null && token.l2_registration_block !== undefined,
  };
}

// Query keys
export const allTokensQueryKey = () => ["all-tokens"];
export const bridgedTokensQueryKey = () => ["bridged-tokens"];
export const tokenDetailsQueryKey = (address: Address) => ["token-details", address];
const tokenCursorKey = () => ["token-cursor"];

// Helper to get the highest non-bridged token ID
function getHighestNonBridgedTokenId(tokens: ApiToken[]): number | undefined {
  const nonBridgedTokens = tokens.filter(
    (token) => !token.l2_registration_block || token.l2_registration_block === null,
  );

  if (nonBridgedTokens.length === 0) return undefined;

  const ids = nonBridgedTokens.map((token) => token.id).filter((id): id is number => id !== undefined && id !== null);

  return ids.length > 0 ? Math.max(...ids) : undefined;
}

// Fetch all tokens with cursor support for incremental updates
export async function fetchAllTokens(
  cursor?: number,
  options?: { cache?: RequestCache },
): Promise<{ tokens: TokenDetails[]; nextCursor?: number }> {
  const l1Client = await getL1ClientAsync();
  const apiTokens = await getAllTokens(l1Client, { startCursor: cursor, cache: options?.cache });
  const tokens = await Promise.all(
    apiTokens.map(async (token) => {
      const mapped = mapApiTokenToTokenDetails(token);

      // For ACCEPTED tokens that aren't showing as registered yet,
      // do a direct chain check in case the API hasn't caught up
      if (mapped.l1ProposalStatus === TokenProposalStatus.ACCEPTED && !mapped.l1RegistrationStatus) {
        try {
          const l1Portal = await getL1Portal();
          const isRegistered = await l1Portal.isRegistered(mapped.tokenAddress);
          if (isRegistered) {
            console.log(`Direct chain check: Token ${mapped.tokenAddress} is registered on L1 (API not yet updated)`);
            mapped.l1RegistrationStatus = true;
          }
        } catch (error) {
          console.error(`Failed to check L1 registration status for ${mapped.tokenAddress}:`, error);
        }
      }

      return mapped;
    }),
  );
  const nextCursor = getHighestNonBridgedTokenId(apiTokens);
  return { tokens, nextCursor };
}

// Hook to get all tokens with smart cursor-based updates
export function useAllTokens() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: allTokensQueryKey(),
    queryFn: async () => {
      // Get the stored cursor from previous fetches
      const storedCursor = queryClient.getQueryData<number>(tokenCursorKey());
      const { tokens, nextCursor } = await fetchAllTokens(storedCursor);

      // If we have a cursor, merge with existing data
      if (storedCursor && storedCursor > 0) {
        const existingTokens = queryClient.getQueryData<TokenDetails[]>(allTokensQueryKey()) || [];

        // Create a map for efficient lookups
        const tokenMap = new Map(existingTokens.map((t) => [t.tokenAddress.toLowerCase(), t]));

        // Update or add new tokens
        for (const token of tokens) {
          tokenMap.set(token.tokenAddress.toLowerCase(), token);
        }

        // Store the new cursor for next time
        if (nextCursor) {
          queryClient.setQueryData(tokenCursorKey(), nextCursor);
        }

        return Array.from(tokenMap.values());
      }

      // First fetch or no cursor - store cursor and return all tokens
      if (nextCursor) {
        queryClient.setQueryData(tokenCursorKey(), nextCursor);
      }
      return tokens;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - reasonable for blockchain operations
    gcTime: Number.POSITIVE_INFINITY, // Keep forever - blockchain data is permanent
  });
}

// Fetch only bridged tokens
export async function fetchBridgedTokens(): Promise<TokenDetails[]> {
  console.debug("Fetching bridged tokens from API");
  const l1Client = await getL1ClientAsync();
  const tokens = await getAllBridgedTokens(l1Client);
  console.debug(`Fetched ${tokens.length} bridged tokens from API`);
  return tokens.map(mapApiTokenToTokenDetails);
}

// Hook to get only fully bridged tokens
export function useBridgedTokens() {
  const { isConnected } = useAccount();
  const queryClient = useQueryClient();
  const previousConnectionRef = useRef(isConnected);

  useEffect(() => {
    const wasConnected = previousConnectionRef.current;

    if (isConnected && !wasConnected) {
      queryClient.invalidateQueries({ queryKey: bridgedTokensQueryKey() });
    } else if (!isConnected && wasConnected) {
      queryClient.removeQueries({ queryKey: bridgedTokensQueryKey() });
    }

    previousConnectionRef.current = isConnected;
  }, [isConnected, queryClient]);

  return useQuery({
    queryKey: bridgedTokensQueryKey(),
    queryFn: fetchBridgedTokens,
    enabled: isConnected,
    staleTime: Number.POSITIVE_INFINITY, // Bridged tokens never change
    gcTime: Number.POSITIVE_INFINITY, // Keep in cache forever
  });
}

export function usePortalTokenList() {
  console.debug("usePortalTokenList called");
  const { data: allTokens, ...rest } = useAllTokens();

  // Return just the token addresses like the original function did
  const tokenAddresses = allTokens?.map((token) => token.tokenAddress) ?? [];

  return {
    ...rest,
    data: tokenAddresses,
  };
}

// Manual refresh function for individual tokens
export function useRefreshTokenDetails() {
  const queryClient = useQueryClient();

  return async (address: Address) => {
    // Fetch fresh data with chain check
    const freshData = await fetchSingleTokenWithChainCheck(address);

    if (freshData) {
      // Update the token-details cache
      queryClient.setQueryData<TokenDetails>(tokenDetailsQueryKey(address), freshData);

      // Update this token in the all-tokens cache
      const allTokens = queryClient.getQueryData<TokenDetails[]>(allTokensQueryKey());
      if (allTokens) {
        const updatedTokens = allTokens.map((t) =>
          t.tokenAddress.toLowerCase() === address.toLowerCase() ? freshData : t,
        );
        queryClient.setQueryData(allTokensQueryKey(), updatedTokens);
      }
    }

    // Invalidate to trigger re-renders
    await queryClient.invalidateQueries({ queryKey: tokenDetailsQueryKey(address) });

    return freshData;
  };
}

export function useTokenDetails(address: Address) {
  console.debug("useTokenDetails called for address:", address);
  const isConnected = useStore(isAztecConnected);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: tokenDetailsQueryKey(address),
    queryFn: async (): Promise<TokenDetails> => {
      console.debug("Getting token details for:", address);

      if (!isConnected) {
        throw new Error("Aztec wallet not connected");
      }

      if (!address) {
        throw new Error("Invalid token address");
      }

      // Try to fetch the individual token with chain check
      const tokenDetails = await fetchSingleTokenWithChainCheck(address);
      if (tokenDetails) {
        console.debug("Found token:", tokenDetails);

        // Also update this token in the all-tokens cache if it exists
        const allTokens = queryClient.getQueryData<TokenDetails[]>(allTokensQueryKey());
        if (allTokens) {
          const updatedTokens = allTokens.map((t) =>
            t.tokenAddress.toLowerCase() === address.toLowerCase() ? tokenDetails : t,
          );
          queryClient.setQueryData(allTokensQueryKey(), updatedTokens);
        }

        return tokenDetails;
      }

      throw new Error(`Token ${address} not found`);
    },
    enabled: !!address && isConnected === true,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - reasonable for blockchain operations
    gcTime: Number.POSITIVE_INFINITY, // Keep forever - blockchain data is permanent
  });
}

// Fetch a single token's status with direct chain check
export async function fetchSingleTokenWithChainCheck(address: Address): Promise<TokenDetails | null> {
  const l1Client = await getL1ClientAsync();

  // Fetch only this specific token from the API
  const apiToken = await getTokenByAddress(address, l1Client, { cache: "no-cache" });
  if (!apiToken) return null;

  const tokenDetails = mapApiTokenToTokenDetails(apiToken);

  // For ACCEPTED tokens, always do a direct chain check
  if (tokenDetails.l1ProposalStatus === TokenProposalStatus.ACCEPTED && !tokenDetails.l1RegistrationStatus) {
    try {
      const l1Portal = await getL1Portal();
      const isRegistered = await l1Portal.isRegistered(address);
      if (isRegistered) {
        console.log(`Direct chain check: Token ${address} is registered on L1`);
        tokenDetails.l1RegistrationStatus = true;
      }
    } catch (error) {
      console.error(`Failed to check L1 registration status for ${address}:`, error);
    }
  }

  return tokenDetails;
}

export function useTokenDetailsList(addresses: Address[]) {
  console.debug("useTokenDetailsList called for addresses:", addresses);
  const isConnected = useStore(isAztecConnected);
  const { data: allTokens, isPending, error, refetch } = useAllTokens();

  // Simply filter the cached tokens by the requested addresses
  const filteredTokens =
    allTokens?.filter((token) => addresses.some((addr) => addr.toLowerCase() === token.tokenAddress.toLowerCase())) ??
    [];

  return {
    data: isConnected ? filteredTokens : [],
    isPending: isPending || !isConnected,
    error: !isConnected ? new Error("Aztec wallet not connected") : error,
    refetch,
  };
}

// Legacy function for backward compatibility - now uses the new unified query
export function useProposedTokensQuery() {
  console.debug("useProposedTokensQuery called");
  // Simply return all tokens from the API
  return useAllTokens();
}

export function useRegisteredTokens() {
  console.debug("useRegisteredTokens called");
  // Use the optimized bridged tokens query instead of filtering all tokens
  const { isConnected } = useAccount();
  const { data: bridgedTokens, isLoading, isFetching, error } = useBridgedTokens();
  const derivedLoading = !isConnected || isLoading || isFetching;
  console.debug("Bridged tokens:", bridgedTokens, "isLoading:", derivedLoading, "walletConnected:", isConnected);
  return {
    data: bridgedTokens,
    isLoading: derivedLoading,
    error: isConnected ? error : undefined,
  };
}

// Get cached token info from the turnstile API data
// This replaces direct blockchain queries for ERC20 token info
export async function getCachedTokenInfo(l1Address: string): Promise<{
  name: string;
  symbol: string;
  decimals: number;
  l1Address: string;
}> {
  const l1Client = await getL1ClientAsync();

  // Try to get from cache first, but allow fresh fetch if needed
  // Using default cache behavior to allow updates during registration
  const apiTokens = await getAllTokens(l1Client);

  // Find the token by L1 address
  const token = apiTokens.find((t) => t.l1_address?.toLowerCase() === l1Address.toLowerCase());

  if (!token) {
    // Token not in API yet - this can happen during registration flow
    // Fall back to blockchain query in this case
    console.warn(`Token ${l1Address} not in turnstile API cache yet, falling back to blockchain query`);
    const { getErc20TokenInfo } = await import("./ethereum");
    return getErc20TokenInfo(l1Address as `0x${string}`);
  }

  return {
    name: token.name || "",
    symbol: token.symbol || "",
    decimals: token.decimals || 18,
    l1Address: token.l1_address || l1Address,
  };
}
